import { FC, useEffect, useState } from "react"
import { useRouter } from "next/router"
import { LoadingDots } from "@components/ui"
import { Grid } from "@theme-ui/components"
import { ProductCardProps } from "@components/common/ProductCard"
import { ProductCardDemo, ProductCard } from "@components/common"
import { IncludedItem, Product } from "@lib/swell/storefront-data-hooks/src/types"
import Link from "next/link"
import { getPrice } from "@lib/swell/storefront-data-hooks/src/utils/product"
import classNames from "classnames"

import {
  getCollection,
  getProduct,
} from "@lib/swell/storefront-data-hooks/src/api/operations-swell"
import builderConfig from "@config/builder"
import Image from "next/image"
interface HighlightedCardProps extends Omit<ProductCardProps, "product"> {
  index: number
}
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid"

export interface PaginationProps {
  start: number
  resultCount: number
  total: number
  page: number
  pages: any
}

export const Pagination: FC<PaginationProps> = ({
  start = 1,
  resultCount = 10,
  total = 10,
  page = 1,
  pages,
}) => {
  const router = useRouter()
  const prevHref = {
    pathname: `/collection/${router.query.handle}`,
    query: { page: page - 1 },
  }
  const nextHref = {
    pathname: `/collection/${router.query.handle}`,
    query: { page: page + 1 },
  }
  const lastPage = pages && Object.keys(pages).length === page
  const onlyPage = total < 15

  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
      <div className="flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{start}</span> to{" "}
            <span className="font-medium">{start + resultCount - 1}</span> of{" "}
            <span className="font-medium">{total}</span> results
          </p>
        </div>
        <div>
          <nav
            className="isolate inline-flex -space-x-px rounded-md shadow-xs"
            aria-label="Pagination"
          >
            <Link
              href={prevHref}
              passHref
              className={classNames([
                "relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0",
                page === 1 || !page ? "pointer-events-none" : "",
              ])}
            >
              <div>
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              </div>
            </Link>
            {/* Current: "z-10 bg-primary-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600", Default: "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0" */}
            {pages &&
              Object.keys(pages).map((pageNumber: any) => {
                const current = page.toString() === pageNumber
                const href = {
                  pathname: `/collection/${router.query.handle}`,
                  query: { page: pageNumber },
                }

                if (current) {
                  return (
                    <Link
                      key={pageNumber}
                      href={href}
                      aria-current="page"
                      className="relative z-10 inline-flex items-center bg-primary-600 px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                    >
                      {pageNumber}
                    </Link>
                  )
                } else {
                  return (
                    <Link
                      key={pageNumber}
                      href={href}
                      className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                    >
                      {pageNumber}
                    </Link>
                  )
                }
              })}
            <Link
              href={nextHref}
              passHref
              className={classNames([
                "relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0",
                lastPage || onlyPage ? "pointer-events-none" : "",
              ])}
            >
              <div>
                <span className="sr-only">Next</span>
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              </div>
            </Link>
          </nav>
        </div>
      </div>
    </div>
  )
}

export interface ProductCardsWithFullImageProps {
  products?: any[]
  productsList?: Array<{ product: string }>
  collection?: string | any
  offset: number
  limit: number
  cardProps: Omit<ProductCardProps, "product">
  highlightCard?: HighlightedCardProps
}

export const ProductCardsWithFullImage: FC<ProductCardsWithFullImageProps> = ({
  products: initialProducts,
  collection,
  productsList,
  offset = 0,
  limit = 10,
  cardProps,
  highlightCard,
}) => {
  const [products, setProducts] = useState(initialProducts || [])
  const [loading, setLoading] = useState(false)
  const category = collection.category
  useEffect(() => {
    const getProducts = async () => {
      setLoading(true)
      const promises = productsList!
        .map((entry) => entry.product)
        .filter((handle: string | undefined) => typeof handle === "string")
        .map(async (handle: string) => {
          return await getProduct({ slug: handle })
        })
      const result = await Promise.all(promises)
      setProducts(result)
      setLoading(false)
    }
    if (productsList && !initialProducts) {
      getProducts()
    }
  }, [productsList, initialProducts])

  if (loading) {
    return <LoadingDots />
  }

  const ProductComponent: any = process.env.IS_DEMO ? ProductCardDemo : ProductCard
  return (
    <div className="bg-white px-16 pb-5 sm:p-5">
      <div className="">
        <h2 className="sr-only">Products</h2>

        <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:gap-x-8">
          {products.map((product: Product) => (
            <Link key={product.id} href={`/product/${product.slug}/`}>
              <div className="group rounded-lg border border-gray-200 sm:rounded-none sm:border-none">
                <div className="aspect-h-2 aspect-w-3 w-full overflow-hidden rounded-b-none rounded-t-lg bg-white sm:rounded-lg">
                  {product.images[0] && (
                    <Image
                      fill={true}
                      src={product.images[0]?.src}
                      alt={product.images[0]?.metadata}
                      className="h-full w-full object-cover object-center hover:opacity-75"
                    />
                  )}
                </div>
                <div className="flex flex-1 flex-col space-y-2 p-4">
                  <h3 className="text-sm text-gray-700">{product.name}</h3>
                  <div className="flex flex-1 flex-col justify-end">
                    {product.sale ? (
                      <div>
                        <p className="text-base font-medium text-gray-900 line-through">
                          {getPrice(product.orig_price, "USD")}
                        </p>
                        <div className="flex space-x-2">
                          <p className="font-medium text-gray-900 text-red-600">
                            {getPrice(product.price, "USD")}
                          </p>
                          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                            SAVE {getPrice(product.orig_price - product.price, "USD")}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-base font-medium text-gray-900">
                        {getPrice(product.price, "USD")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <Pagination
        start={collection.page * limit - limit + 1}
        resultCount={collection.products.length}
        total={collection.count}
        page={collection.page}
        pages={collection.pages}
      />
    </div>
  )
}
